import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { db } from "../firebase/client";
import type { BroadcastSummary } from "../types/broadcasts.types";

export function useFollowingBroadcasts(uid: string | null): BroadcastSummary[] {
	const [followedUids, setFollowedUids] = useState<string[]>([]);
	const [broadcasts, setBroadcasts] = useState<BroadcastSummary[]>([]);
	const broadcastUnsubRef = useRef<(() => void) | null>(null);

	// Subscribe to the followed authors list
	useEffect(() => {
		if (!uid) {
			setFollowedUids([]);
			return;
		}

		const q = collection(db, "users", uid, "following");
		return onSnapshot(q, (snap) => {
			setFollowedUids(snap.docs.map((d) => d.id));
		});
	}, [uid]);

	// Subscribe to broadcasts from followed authors
	useEffect(() => {
		broadcastUnsubRef.current?.();
		broadcastUnsubRef.current = null;

		if (followedUids.length === 0) {
			setBroadcasts([]);
			return;
		}

		// Firestore 'in' supports up to 10 values
		const batch = followedUids.slice(0, 10);
		const q = query(collection(db, "broadcasts"), where("userId", "in", batch));

		broadcastUnsubRef.current = onSnapshot(q, (snap) => {
			setBroadcasts(
				snap.docs
					.map((doc) => ({ id: doc.id, ...(doc.data() as Omit<BroadcastSummary, "id">) }))
					.filter((b) => b.status === "live" || b.status === "upcoming"),
			);
		});

		return () => {
			broadcastUnsubRef.current?.();
			broadcastUnsubRef.current = null;
		};
	}, [followedUids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

	return broadcasts;
}
